import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';
import { createTmpUploadedImgs, createTmpUploadedFiles } from '../../../handler/ChatAIHandler';
import { createTmpImageObjects } from './UploadImageHandler';
import { createTmpFileObjects } from './UploadFileHandler';

/**
 * Check if a file name is an image by its extension (case-insensitive).
 * Files without an extension are treated as documents.
 * @param {string} fileName - File name
 * @returns {boolean}
 */
const isImageFileName = (fileName) => {
    if (!fileName) return false;
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex <= 0) return false;
    const extension = fileName.substring(dotIndex + 1).toLowerCase();
    return Constant.AI_CHAT_ATTACHMENT.IMAGE_EXTENSIONS.includes(extension);
};

const showErrorSnackbar = (enqueueSnackbar, messageKey) => {
    enqueueSnackbar?.(buildSnackbarMessage(messageKey), {
        variant: 'error',
        autoHideDuration: 2000
    });
};

/**
 * Validate a batch of new files against the attachment limits.
 * The whole batch is rejected if any limit is exceeded. Count is checked before size.
 * @param {Object} params
 * @param {File[]} params.newFiles - Files to add
 * @param {Object[]} params.uploadImgs - Pending image items
 * @param {Object[]} params.uploadFiles - Pending document items
 * @param {Function} params.enqueueSnackbar - Snackbar function
 * @returns {boolean} true if the batch can be accepted
 */
const validateAttachmentBatch = ({ newFiles = [], uploadImgs = [], uploadFiles = [], enqueueSnackbar }) => {
    if (Utils.isEmptyArray(newFiles)) return false;

    const { MAX_COUNT, MAX_FILE_SIZE } = Constant.AI_CHAT_ATTACHMENT;
    const totalCount = uploadImgs.length + uploadFiles.length + newFiles.length;
    if (totalCount > MAX_COUNT) {
        showErrorSnackbar(enqueueSnackbar, 'INVALID_SEND_AI_TOO_MANY_FILE');
        return false;
    }

    if (newFiles.some(file => file.size > MAX_FILE_SIZE)) {
        showErrorSnackbar(enqueueSnackbar, 'INVALID_SEND_AI_TOO_LARGE_FILE');
        return false;
    }

    return true;
};

/**
 * Accept files from any source (picker, drag & drop, paste).
 * Validate the batch, split into images and documents, create UPLOADING items and start uploading.
 * @param {File[]} newFiles - Files to add
 * @param {Object} ctx - Context
 * @param {Object[]} ctx.uploadImgs - Pending image items
 * @param {Object[]} ctx.uploadFiles - Pending document items
 * @param {Function} ctx.setUploadImgs - Set image items
 * @param {Function} ctx.setUploadFiles - Set document items
 * @param {Function} ctx.enqueueSnackbar - Snackbar function
 * @param {Object} ctx.corporationDetail - Corporation detail
 * @param {Object} ctx.projectInuseDetail - Project in use detail
 * @param {string} ctx.chatThreadId - Chat thread ID
 * @param {Object} [ctx.inputRef] - Input ref to focus after accepting
 * @returns {boolean} true if the batch was accepted
 */
const acceptAttachments = (newFiles, ctx) => {
    const {
        uploadImgs = [],
        uploadFiles = [],
        setUploadImgs,
        setUploadFiles,
        enqueueSnackbar,
        corporationDetail,
        projectInuseDetail,
        chatThreadId,
        inputRef
    } = ctx;

    if (!validateAttachmentBatch({ newFiles, uploadImgs, uploadFiles, enqueueSnackbar })) return false;

    const imageFiles = newFiles.filter(file => isImageFileName(file.name));
    const documentFiles = newFiles.filter(file => !isImageFileName(file.name));

    if (!Utils.isEmptyArray(imageFiles)) {
        const imgs = createTmpImageObjects(imageFiles);
        setUploadImgs(prev => [...prev, ...imgs]);
        createTmpUploadedImgs(corporationDetail.id, projectInuseDetail.id, chatThreadId, imgs, setUploadImgs);
    }

    if (!Utils.isEmptyArray(documentFiles)) {
        const files = createTmpFileObjects(documentFiles);
        setUploadFiles(prev => [...prev, ...files]);
        createTmpUploadedFiles(corporationDetail.id, projectInuseDetail.id, chatThreadId, files, setUploadFiles);
    }

    inputRef?.current?.focus();
    return true;
};

/**
 * Handle paste event: take every file in the clipboard as an attachment.
 * @param {ClipboardEvent} event - Clipboard event
 * @param {Object} ctx - Same context as acceptAttachments
 * @returns {boolean} true if the event contained files and was handled
 */
const handlePasteAttachments = (event, ctx) => {
    const files = [];
    for (const item of event.clipboardData?.items || []) {
        if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) files.push(file);
        }
    }

    if (Utils.isEmptyArray(files)) return false;

    event.preventDefault();
    acceptAttachments(files, ctx);
    return true;
};

export {
    isImageFileName,
    validateAttachmentBatch,
    acceptAttachments,
    handlePasteAttachments
};
